import type { TFunction } from "i18next";
import { differenceInMinutes, differenceInHours, differenceInCalendarDays } from "date-fns";
import useTranslation from "@hooks/use_translation";

export type TimeAgoFormat = "long" | "short";

export const TIME_AGO_SEPARATOR = "·";

export const timeAgo = (
  timestamp: string | Date | null | undefined,
  t: TFunction,
  format: TimeAgoFormat = "long",
): string => {
  if (!timestamp) {
    return "";
  }

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const now = new Date();
  const key = (base: string) => (format === "short" ? `${base}Short` : base);

  const minutes = differenceInMinutes(now, date);
  if (minutes < 5) {
    return t(key("justNow"));
  }
  if (minutes < 60) {
    return t(key("minutes"), { count: minutes });
  }

  const calendarDays = differenceInCalendarDays(now, date);
  if (calendarDays === 0) {
    return t(key("hours"), { count: differenceInHours(now, date) });
  }
  if (calendarDays <= 6) {
    return t(key("days"), { count: calendarDays });
  }
  if (calendarDays <= 13) {
    return t(key("lastWeek"));
  }
  if (calendarDays <= 34) {
    return t(key("weeks"), { count: Math.floor(calendarDays / 7) });
  }
  return t(key("months"), { count: Math.floor(calendarDays / 30) });
};

export const useTimeAgo = (
  timestamp: string | Date | null | undefined,
  format: TimeAgoFormat = "long",
): string => {
  const { t } = useTranslation("timeAgo");
  return timeAgo(timestamp, t, format);
};

export const composeStatusLabel = (action: string, timeAgoText: string): string =>
  timeAgoText ? `${action} ${TIME_AGO_SEPARATOR} ${timeAgoText}` : action;
